// Fatia S1: perfil do usuário, senha, 2FA e aparelhos conectados.
import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Laptop, MailWarning, Smartphone } from 'lucide-react'
import type { SessionsResponse } from '@impulso/shared/api/identity'
import { api, errorMessage } from '../../lib/api'
import { useSession } from '../../lib/session'
import { useToast } from '../../ui/toast'
import { ErrorState, Loading, PageHead } from '../../ui'
import { FormError } from '../../features/auth/AuthShell'
import PasswordInput from '../../features/auth/PasswordInput'
import ChangeEmailCard from '../../features/auth/ChangeEmailCard'
import TwoFactorCard from '../../features/auth/TwoFactorCard'
import DeleteAccountCard from '../../features/account/DeleteAccountCard'
import './profile.css'

function deviceName(ua: string | null) {
  if (!ua) return 'Aparelho desconhecido'
  const os = /iPhone|iPad/.test(ua) ? 'iPhone/iPad' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : 'Outro'
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Navegador'
  return `${browser} no ${os}`
}

export default function Profile() {
  const { me, refresh } = useSession()
  const toast = useToast()
  const qc = useQueryClient()
  const [name, setName] = useState(me?.user.name ?? '')
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [nameErr, setNameErr] = useState<string | null>(null)
  const [passErr, setPassErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: () => api.get<SessionsResponse>('/auth/sessions') })

  if (!me) return <Loading />

  async function saveName(e: FormEvent) {
    e.preventDefault()
    setNameErr(null)
    setBusy('name')
    try { await api.patch('/auth/profile', { name }); await refresh(); toast('Nome atualizado.') } catch (err) { setNameErr(errorMessage(err)) } finally { setBusy(null) }
  }
  async function changePassword(e: FormEvent) {
    e.preventDefault()
    setPassErr(null)
    if (next.length < 8) return setPassErr('A nova senha precisa ter pelo menos 8 caracteres.')
    setBusy('pass')
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next })
      setCurrent(''); setNext('')
      toast('Senha alterada. Os outros aparelhos foram desconectados.')
      qc.invalidateQueries({ queryKey: ['sessions'] })
    } catch (err) { setPassErr(errorMessage(err)) } finally { setBusy(null) }
  }
  async function resend() {
    setBusy('verify')
    try { await api.post('/auth/resend-verification'); toast(`Enviamos um novo link para ${me!.user.email}.`) } catch (err) { toast(errorMessage(err)) } finally { setBusy(null) }
  }
  async function revoke(id: string) {
    try { await api.del(`/auth/sessions/${id}`); toast('Aparelho desconectado.'); qc.invalidateQueries({ queryKey: ['sessions'] }) } catch (err) { toast(errorMessage(err)) }
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Meu perfil e segurança" sub={me.user.email} />
      {!me.user.emailVerified && (
        <div className="callout amber profile-verify" role="status">
          <MailWarning size={18} color="var(--amber)" style={{ flexShrink: 0, marginTop: 2 }} />
          <span style={{ flex: 1 }}>Seu e-mail ainda não foi confirmado. Confirme para receber avisos importantes e conseguir recuperar a senha.</span>
          <button className="btn sm" disabled={busy === 'verify'} onClick={resend}>Reenviar link</button>
        </div>
      )}
      <div className="grid g-2">
        <form className="card stack" style={{ gap: 14 }} onSubmit={saveName}>
          <h2>Seus dados</h2>
          <FormError error={nameErr} />
          <div className="field"><label htmlFor="pf-name">Nome</label><input id="pf-name" className="input" value={name} onChange={(e) => setName(e.target.value)} minLength={2} required /></div>
          <div className="field"><label htmlFor="pf-email">E-mail</label><input id="pf-email" className="input" value={me.user.email} disabled /><span className="small muted">Para mudar, use “Trocar e-mail” abaixo.</span></div>
          <button className="btn primary" style={{ alignSelf: 'flex-start' }} disabled={busy === 'name'}>Salvar</button>
        </form>
        <form className="card stack" style={{ gap: 14 }} onSubmit={changePassword}>
          <h2>Trocar senha</h2>
          <FormError error={passErr} />
          <div className="field"><label htmlFor="pf-cur">Senha atual</label><PasswordInput id="pf-cur" value={current} onChange={setCurrent} autoComplete="current-password" /></div>
          <div className="field"><label htmlFor="pf-new">Nova senha</label><PasswordInput id="pf-new" value={next} onChange={setNext} autoComplete="new-password" placeholder="Pelo menos 8 caracteres" /></div>
          <button className="btn primary" style={{ alignSelf: 'flex-start' }} disabled={busy === 'pass'}>Trocar senha</button>
        </form>
      </div>
      <ChangeEmailCard />
      <TwoFactorCard />
      <div className="card">
        <h2>Aparelhos conectados</h2>
        <p className="small muted mt-8">Se não reconhecer algum, desconecte e troque sua senha.</p>
        <div className="list mt-16">
          {sessions.isLoading && <Loading lines={2} />}
          {sessions.error && <ErrorState error={sessions.error} onRetry={() => sessions.refetch()} />}
          {sessions.data?.items.map((s) => (
            <div key={s.id} className="list-item" style={{ alignItems: 'center' }}>
              <span className="ic purple">{/iPhone|Android/.test(s.userAgent ?? '') ? <Smartphone size={17} /> : <Laptop size={17} />}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="strong">{deviceName(s.userAgent)} {s.current && <span className="badge green" style={{ marginLeft: 6 }}>Este aparelho</span>}</div>
                <div className="small muted">Último acesso em {new Date(s.lastSeenAt).toLocaleString('pt-BR')}{s.ip ? ` · IP ${s.ip}` : ''}</div>
              </div>
              {!s.current && <button className="btn sm" onClick={() => revoke(s.id)}>Desconectar</button>}
            </div>
          ))}
        </div>
      </div>
      {me.accounts.filter((a) => a.role === 'owner').map((a) => <DeleteAccountCard key={a.id} account={a} />)}
    </div>
  )
}
